import type { Alert } from "../../shared/alerts.js";
import type { AppContext } from "../Page.js";
import { h, patch } from "../dom.js";
import { ago } from "../format.js";

/** Popups shown at once; the rest are counted under them. */
const MAX_SHOWN = 4;

/**
 * The header bell and the alert popups. Popups stay on screen, on every page,
 * until clicked; a click dismisses the alert for everyone.
 */
export class AlertCenter {
  readonly bell: HTMLButtonElement;
  readonly stack = h("div", { class: "alert-stack", role: "region", "aria-label": "Alerts" });
  private readonly count = h("span", { class: "alert-count" });
  private readonly dismissing = new Set<number>();

  constructor(private readonly ctx: AppContext) {
    this.bell = h("button", { type: "button", class: "alert-bell", title: "Alerts", onclick: () => ctx.navigate("alerts") }, "🔔", this.count);
    ctx.live.on("alerts", () => this.render());
    this.render();
  }

  private visible(): Alert[] {
    return this.ctx.live.alerts.filter((a) => !this.dismissing.has(a.id));
  }

  private async dismiss(ids: number[] | "all"): Promise<void> {
    const targets = ids === "all" ? this.visible().map((a) => a.id) : ids;
    for (const id of targets) this.dismissing.add(id);
    this.render();
    try {
      const { dismissed } = await this.ctx.api.dismissAlerts(ids);
      this.ctx.live.dropAlerts(dismissed);
    } catch (err) {
      this.ctx.toast((err as Error).message, "error");
    } finally {
      for (const id of targets) this.dismissing.delete(id);
      this.render();
    }
  }

  /** Redraws the bell, the popups and the count in the tab title. */
  render(): void {
    const alerts = this.visible();
    const n = alerts.length;
    this.count.textContent = n ? String(n) : "";
    this.bell.classList.toggle("has-alerts", n > 0);
    this.bell.classList.toggle("has-errors", alerts.some((a) => a.level === "error" && a.active));
    this.bell.title = n ? `${n} alert${n === 1 ? "" : "s"}: open the Alerts page` : "No alerts: choose what to watch on the Alerts page";
    document.title = document.title.replace(/^\(\d+\) /, "");
    if (n) document.title = `(${n}) ${document.title}`;

    // The Alerts page lists them all, so the popups would only cover it.
    if (location.hash.replace(/^#\/?/, "") === "alerts") {
      patch(this.stack);
      return;
    }
    const cards = alerts.slice(0, MAX_SHOWN).map((a) => this.card(a));
    const more = n > MAX_SHOWN
      ? h(
          "div",
          { class: "alert-more" },
          h("button", { type: "button", class: "link", onclick: () => this.ctx.navigate("alerts") }, `${n - MAX_SHOWN} more`),
          h("button", { type: "button", class: "link", onclick: () => void this.dismiss("all") }, "Dismiss all"),
        )
      : null;
    patch(this.stack, ...cards, ...(more ? [more] : []));
  }

  private card(a: Alert): HTMLElement {
    return h(
      "button",
      {
        key: a.id,
        type: "button",
        class: `alert-card level-${a.level}${a.active ? "" : " settled"}`,
        title: "Click to dismiss",
        onclick: () => void this.dismiss([a.id]),
      },
      h(
        "span",
        { class: "alert-head" },
        h("strong", null, a.title),
        a.count > 1 ? h("span", { class: "badge" }, `×${a.count}`) : null,
        a.active ? null : h("span", { class: "badge ok" }, "Cleared"),
      ),
      h("span", { class: "alert-msg" }, a.msg),
      h("span", { class: "muted small" }, `${ago(a.lastAt)} · click to dismiss`),
    );
  }
}
